import {
  Children,
  cloneElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../utils/cn';

// Tooltip — texto breve asociado a un control, accesible con mouse, teclado y táctil.
//
// Complementa a InfoHint (que tiene su propio botón ⓘ): Tooltip envuelve un disparador
// que ya existe (un botón de ícono, un badge, una hora relativa) y le agrega la ayuda
// sin cambiar su aspecto. Regla de uso: nada esencial vive solo en un tooltip.
//
// Cómo se abre:
//   · puntero fino (mouse o lápiz): a los `delay` ms de entrar; cierra 100 ms después de
//     salir. El panel es "hoverable" (WCAG 1.4.13): pasar el puntero sobre él no lo cierra;
//   · teclado: al recibir foco, solo si el disparador cumple :focus-visible (un clic de
//     mouse no lo abre); cierra con blur;
//   · táctil: `tap` alterna y deja el panel fijado; `longpress` abre a los 500 ms y
//     suprime UN click posterior, para que leer la ayuda no dispare la acción. En `auto`
//     se usa `longpress` si el hijo es una acción y `tap` si no. Se cierra al tocar fuera;
//     no se cierra solo con un temporizador.
//
// Escape se escucha en `document` en fase de CAPTURA: si el tooltip está abierto lo cierra
// y consume el evento, para que no cierre también el Modal, el Drawer o la hoja que lo
// contiene (esos escuchan en window, en burbuja). Cerrado, no toca el Escape.
//
// Uno abierto a la vez: abrir uno cierra el anterior (store de módulo).
//
// Accesibilidad: el texto se renderiza siempre en un <span hidden> junto al disparador y
// este lo referencia con `aria-describedby` (un nodo oculto pero referenciado entra en la
// descripción accesible). La copia visual va en un portal con `aria-hidden`, para que el
// lector de pantalla no la lea dos veces. Ese <span> queda como hermano SIGUIENTE del
// disparador: un `last:` o `:last-child` aplicado al disparador deja de cumplirse.
//
// Props:
//   content    ReactNode (obligatorio) — texto del tooltip; vacío = sin tooltip
//   children   un único elemento que reciba el ref en un nodo del DOM: <button>, <a> o un
//              elemento con tabIndex=0 (o un componente con forwardRef, como ui/Button)
//   side?      'top' | 'bottom' | 'left' | 'right' (default 'top'); cambia al lado opuesto
//              si no cabe
//   align?     'center' | 'start' | 'end' (default 'center')
//   delay?     ms antes de abrir con hover (default 400)
//   describe?  false cuando el texto repite el aria-label del disparador (botones de ícono)
//   touch?     'auto' | 'tap' | 'longpress' (default 'auto')
//   disabled?  desactiva el tooltip sin desmontar el disparador
//   className? clases extra del panel visual
//
// El disparador NO debe llevar `disabled`: un botón deshabilitado no emite eventos ni
// recibe foco, así que el tooltip nunca se vería. Para eso está `aria-disabled="true"`.

const LONG_PRESS_MS = 500;
const HOVER_CLOSE_GRACE_MS = 100;
// Separación entre el disparador y el panel, y margen mínimo contra los bordes del viewport.
const GAP_PX = 6;
const VIEWPORT_MARGIN_PX = 8;

const SIDES = ['top', 'bottom', 'left', 'right'];
const ALIGNS = ['center', 'start', 'end'];
const OPPOSITE_SIDE = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };

const ACTION_TAGS = new Set(['BUTTON', 'SUMMARY']);
const ACTION_INPUT_TYPES = new Set(['button', 'submit', 'reset', 'image']);
const ACTION_ROLES = new Set([
  'button',
  'link',
  'menuitem',
  'menuitemcheckbox',
  'menuitemradio',
  'tab',
  'switch',
  'checkbox',
  'radio',
  'option',
]);

// ── Uno a la vez ──────────────────────────────────────────────────────────────────────
// Store de módulo con el tooltip abierto: { id, close }. El id (useId) identifica a la
// instancia; abrir otra cierra la anterior.
let activeTooltip = null;

function claimActiveTooltip(id, close) {
  if (activeTooltip && activeTooltip.id !== id) activeTooltip.close();
  activeTooltip = { id, close };
}

function releaseActiveTooltip(id) {
  if (activeTooltip?.id === id) activeTooltip = null;
}

// ── Posicionamiento (puro, sin DOM) ───────────────────────────────────────────────────
function spaceOnSide(side, rect, viewportWidth, viewportHeight) {
  if (side === 'top') return rect.top;
  if (side === 'bottom') return viewportHeight - rect.bottom;
  if (side === 'left') return rect.left;
  return viewportWidth - rect.right;
}

function resolveSide(preferred, rect, panelWidth, panelHeight, viewportWidth, viewportHeight) {
  const fits = (side) => {
    const needed = side === 'top' || side === 'bottom' ? panelHeight : panelWidth;
    return (
      spaceOnSide(side, rect, viewportWidth, viewportHeight) >=
      needed + GAP_PX + VIEWPORT_MARGIN_PX
    );
  };
  if (fits(preferred)) return preferred;
  const opposite = OPPOSITE_SIDE[preferred];
  if (fits(opposite)) return opposite;
  // No cabe en ninguno de los dos: el que tenga más espacio corta menos.
  return spaceOnSide(opposite, rect, viewportWidth, viewportHeight) >
    spaceOnSide(preferred, rect, viewportWidth, viewportHeight)
    ? opposite
    : preferred;
}

function alignOnAxis(start, size, panelSize, align) {
  if (align === 'start') return start;
  if (align === 'end') return start + size - panelSize;
  return start + size / 2 - panelSize / 2;
}

function clamp(value, min, max) {
  // Si el panel es más grande que el viewport, gana el borde inicial (el texto empieza visible).
  return Math.min(Math.max(value, min), Math.max(min, max));
}

/**
 * Calcula la posición `fixed` del panel respecto del viewport.
 * Exportada para testearla sin depender del layout de jsdom (que mide todo en 0).
 */
export function computeTooltipPosition({
  triggerRect,
  panelWidth,
  panelHeight,
  side = 'top',
  align = 'center',
  viewportWidth,
  viewportHeight,
}) {
  const preferredSide = SIDES.includes(side) ? side : 'top';
  const finalAlign = ALIGNS.includes(align) ? align : 'center';
  const finalSide = resolveSide(
    preferredSide,
    triggerRect,
    panelWidth,
    panelHeight,
    viewportWidth,
    viewportHeight,
  );

  let top;
  let left;
  if (finalSide === 'top' || finalSide === 'bottom') {
    top =
      finalSide === 'top'
        ? triggerRect.top - GAP_PX - panelHeight
        : triggerRect.bottom + GAP_PX;
    left = alignOnAxis(triggerRect.left, triggerRect.width, panelWidth, finalAlign);
  } else {
    left =
      finalSide === 'left'
        ? triggerRect.left - GAP_PX - panelWidth
        : triggerRect.right + GAP_PX;
    top = alignOnAxis(triggerRect.top, triggerRect.height, panelHeight, finalAlign);
  }

  return {
    side: finalSide,
    top: Math.round(
      clamp(top, VIEWPORT_MARGIN_PX, viewportHeight - VIEWPORT_MARGIN_PX - panelHeight),
    ),
    left: Math.round(
      clamp(left, VIEWPORT_MARGIN_PX, viewportWidth - VIEWPORT_MARGIN_PX - panelWidth),
    ),
  };
}

// ── Helpers de DOM ────────────────────────────────────────────────────────────────────
function getViewportSize() {
  const root = document.documentElement;
  // clientWidth/Height excluyen la barra de scroll; si el entorno devuelve 0 (sin layout)
  // se cae a innerWidth/innerHeight.
  return {
    width: root.clientWidth || window.innerWidth,
    height: root.clientHeight || window.innerHeight,
  };
}

function isOutsideViewport(rect, viewportWidth, viewportHeight) {
  return (
    rect.bottom < 0 || rect.top > viewportHeight || rect.right < 0 || rect.left > viewportWidth
  );
}

function matchesFocusVisible(element) {
  try {
    return element.matches(':focus-visible');
  } catch {
    // Navegador sin soporte de :focus-visible (Safari < 15.4): el guard de puntero del
    // llamador ya descartó los focos que llegan por clic o toque.
    return true;
  }
}

// En `touch="auto"`, una acción usa long-press para no robarle el toque a la acción.
// Un control con aria-disabled="true" no hace nada al tocarlo, así que se trata como no
// acción: el tap es la única forma de leer por qué está deshabilitado.
function isActionElement(element) {
  if (!element) return false;
  if (element.getAttribute('aria-disabled') === 'true') return false;
  const role = element.getAttribute('role');
  if (role) return ACTION_ROLES.has(role);
  if (ACTION_TAGS.has(element.tagName)) return true;
  if (element.tagName === 'A') return element.hasAttribute('href');
  if (element.tagName === 'INPUT') return ACTION_INPUT_TYPES.has(element.type);
  return false;
}

function isEmptyContent(content) {
  return content === null || content === undefined || content === false || content === '';
}

function joinIds(...ids) {
  const joined = ids.filter(Boolean).join(' ');
  return joined || undefined;
}

function composeHandlers(childHandler, ownHandler) {
  return (event) => {
    childHandler?.(event);
    ownHandler(event);
  };
}

function clearTimer(timerRef) {
  if (timerRef.current !== null) {
    clearTimeout(timerRef.current);
    timerRef.current = null;
  }
}

export function Tooltip({
  content,
  side = 'top',
  align = 'center',
  delay = 400,
  describe = true,
  touch = 'auto',
  disabled = false,
  className,
  children,
}) {
  const child = Children.only(children);
  const childProps = child.props;
  const childRef = child.ref;
  const inactive = disabled || isEmptyContent(content);

  // Un solo id por instancia: es el del <span> de descripción y la clave en el store.
  const tooltipId = useId();
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);

  // Cómo se abrió ('hover' | 'focus' | 'touch'): decide qué evento lo cierra. Un tooltip
  // abierto por teclado no se cierra porque el mouse salga, ni uno fijado por tap con blur.
  const openModeRef = useRef(null);
  const openTimerRef = useRef(null);
  const closeTimerRef = useRef(null);
  const longPressTimerRef = useRef(null);
  // Hay un puntero presionado sobre el disparador: el foco que llegue en ese momento es
  // de un clic o un toque y no debe abrir el tooltip.
  const pointerPressedRef = useRef(false);
  // Tipo del último pointerdown en el disparador; el click lo consume para saber si fue
  // un toque (un click de teclado no tiene pointerdown previo).
  const lastPointerTypeRef = useRef(null);
  const suppressNextClickRef = useRef(false);

  const hide = useCallback(() => {
    clearTimer(openTimerRef);
    clearTimer(closeTimerRef);
    clearTimer(longPressTimerRef);
    openModeRef.current = null;
    suppressNextClickRef.current = false;
    releaseActiveTooltip(tooltipId);
    setOpen(false);
    setPosition(null);
  }, [tooltipId]);

  const show = useCallback(
    (mode) => {
      clearTimer(openTimerRef);
      clearTimer(closeTimerRef);
      openModeRef.current = mode;
      claimActiveTooltip(tooltipId, hide);
      setOpen(true);
    },
    [tooltipId, hide],
  );

  const setTriggerRef = useCallback(
    (node) => {
      triggerRef.current = node;
      if (typeof childRef === 'function') childRef(node);
      else if (childRef && typeof childRef === 'object') childRef.current = node;
    },
    [childRef],
  );

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const panel = panelRef.current;
    if (!trigger || !panel) return;
    const viewport = getViewportSize();
    const triggerRect = trigger.getBoundingClientRect();
    if (isOutsideViewport(triggerRect, viewport.width, viewport.height)) {
      hide();
      return;
    }
    const panelRect = panel.getBoundingClientRect();
    const next = computeTooltipPosition({
      triggerRect,
      panelWidth: panelRect.width,
      panelHeight: panelRect.height,
      side,
      align,
      viewportWidth: viewport.width,
      viewportHeight: viewport.height,
    });
    setPosition((prev) =>
      prev && prev.top === next.top && prev.left === next.left && prev.side === next.side
        ? prev
        : next,
    );
  }, [side, align, hide]);

  // Se mide antes del primer pintado: el panel se monta invisible en (0,0) y aparece ya
  // en su lugar, sin saltos.
  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open, content, updatePosition]);

  // Reposiciona al hacer scroll (en cualquier contenedor: captura) o al redimensionar,
  // como mucho una vez por frame.
  useEffect(() => {
    if (!open) return undefined;
    let frame = null;
    const scheduleUpdate = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        updatePosition();
      });
    };
    window.addEventListener('scroll', scheduleUpdate, { capture: true, passive: true });
    window.addEventListener('resize', scheduleUpdate);
    return () => {
      window.removeEventListener('scroll', scheduleUpdate, { capture: true });
      window.removeEventListener('resize', scheduleUpdate);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [open, updatePosition]);

  // Escape y toque/clic fuera, solo mientras está abierto.
  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      hide();
    };
    const handlePointerDownOutside = (event) => {
      const target = event.target;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      hide();
    };
    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('pointerdown', handlePointerDownOutside, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('pointerdown', handlePointerDownOutside, true);
    };
  }, [open, hide]);

  useEffect(() => {
    if (inactive) hide();
  }, [inactive, hide]);

  // Al desmontar: sin timers colgados ni una referencia muerta en el store.
  useEffect(
    () => () => {
      clearTimer(openTimerRef);
      clearTimer(closeTimerRef);
      clearTimer(longPressTimerRef);
      releaseActiveTooltip(tooltipId);
    },
    [tooltipId],
  );

  const childDisabled = Boolean(childProps.disabled);
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && childDisabled) {
      console.warn(
        'Tooltip: el disparador tiene `disabled`, así que no recibe foco ni eventos y el ' +
          'tooltip no se verá. Usa aria-disabled="true" y evita la acción en el onClick.',
      );
    }
  }, [childDisabled]);

  const resolveTouchMode = () => {
    if (touch === 'tap' || touch === 'longpress') return touch;
    return isActionElement(triggerRef.current) ? 'longpress' : 'tap';
  };

  const scheduleHoverClose = () => {
    if (openModeRef.current !== 'hover') return;
    clearTimer(closeTimerRef);
    closeTimerRef.current = setTimeout(hide, HOVER_CLOSE_GRACE_MS);
  };

  const handlePointerEnter = (event) => {
    if (event.pointerType === 'touch') return;
    clearTimer(closeTimerRef);
    if (open || openTimerRef.current !== null) return;
    openTimerRef.current = setTimeout(() => {
      openTimerRef.current = null;
      show('hover');
    }, delay);
  };

  const handlePointerLeave = (event) => {
    if (event.pointerType === 'touch') return;
    clearTimer(openTimerRef);
    scheduleHoverClose();
  };

  const handlePointerDown = (event) => {
    pointerPressedRef.current = true;
    lastPointerTypeRef.current = event.pointerType;
    suppressNextClickRef.current = false;

    if (event.pointerType !== 'touch') {
      // Presionar el disparador cancela el hover: la acción suele abrir otra cosa (un
      // Modal, un menú) y el tooltip quedaría flotando encima hasta mover el mouse.
      clearTimer(openTimerRef);
      if (openModeRef.current === 'hover') hide();
      return;
    }

    if (resolveTouchMode() !== 'longpress') return;
    // Un toque corto sobre una acción con el tooltip abierto es para usarla: se cierra.
    if (open) hide();
    clearTimer(longPressTimerRef);
    longPressTimerRef.current = setTimeout(() => {
      longPressTimerRef.current = null;
      suppressNextClickRef.current = true;
      show('touch');
    }, LONG_PRESS_MS);
  };

  const handlePointerUp = () => {
    pointerPressedRef.current = false;
    clearTimer(longPressTimerRef);
  };

  // pointercancel (p. ej. el navegador empezó a hacer scroll) significa que no habrá click:
  // se descarta todo lo que el gesto dejó preparado para él.
  const handlePointerCancel = () => {
    handlePointerUp();
    lastPointerTypeRef.current = null;
    suppressNextClickRef.current = false;
  };

  const handleFocus = (event) => {
    const pressed = pointerPressedRef.current;
    // Se consume aquí: si el puntero se suelta fuera del disparador, el pointerup no llega
    // y el siguiente foco por teclado quedaría bloqueado.
    pointerPressedRef.current = false;
    if (pressed || !matchesFocusVisible(event.currentTarget)) return;
    show('focus');
  };

  const handleBlur = () => {
    if (openModeRef.current === 'focus') hide();
  };

  const handleClick = (event) => {
    const pointerType = lastPointerTypeRef.current;
    lastPointerTypeRef.current = null;

    if (suppressNextClickRef.current) {
      // El click que sigue a un long-press es el mismo gesto de "leer la ayuda": no debe
      // ejecutar la acción del disparador ni burbujear a sus contenedores.
      suppressNextClickRef.current = false;
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    childProps.onClick?.(event);

    if (pointerType !== 'touch' || resolveTouchMode() !== 'tap') return;
    if (open && openModeRef.current === 'touch') hide();
    else show('touch');
  };

  const handlePanelPointerEnter = (event) => {
    if (event.pointerType !== 'touch') clearTimer(closeTimerRef);
  };

  const handlePanelPointerLeave = (event) => {
    if (event.pointerType !== 'touch') scheduleHoverClose();
  };

  const trigger = inactive
    ? child
    : cloneElement(child, {
        ref: setTriggerRef,
        'aria-describedby': describe
          ? joinIds(childProps['aria-describedby'], tooltipId)
          : childProps['aria-describedby'],
        onPointerEnter: composeHandlers(childProps.onPointerEnter, handlePointerEnter),
        onPointerLeave: composeHandlers(childProps.onPointerLeave, handlePointerLeave),
        onPointerDown: composeHandlers(childProps.onPointerDown, handlePointerDown),
        onPointerUp: composeHandlers(childProps.onPointerUp, handlePointerUp),
        onPointerCancel: composeHandlers(childProps.onPointerCancel, handlePointerCancel),
        onFocus: composeHandlers(childProps.onFocus, handleFocus),
        onBlur: composeHandlers(childProps.onBlur, handleBlur),
        onClick: handleClick,
      });

  return (
    <>
      {trigger}
      {!inactive && describe && (
        <span id={tooltipId} hidden>
          {content}
        </span>
      )}
      {open &&
        !inactive &&
        createPortal(
          <div
            ref={panelRef}
            role="tooltip"
            aria-hidden="true"
            onPointerEnter={handlePanelPointerEnter}
            onPointerLeave={handlePanelPointerLeave}
            style={
              position
                ? { top: position.top, left: position.left }
                : { top: 0, left: 0, visibility: 'hidden' }
            }
            className={cn(
              'fixed z-[10000] w-max max-w-[min(18rem,calc(100vw-2rem))] break-words',
              'rounded-md bg-fg text-bg text-xs leading-snug px-2 py-1.5 shadow-lg',
              'transition-opacity duration-100 motion-reduce:transition-none',
              position ? 'opacity-100' : 'opacity-0',
              className,
            )}
          >
            {content}
          </div>,
          document.body,
        )}
    </>
  );
}

Tooltip.displayName = 'Tooltip';

export default Tooltip;
